import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
  Animated,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChevronLeft, Tv } from "lucide-react-native";
import { ParentStackParamList } from "../../navigation/types";
import { pairingApi } from "../../api/pairing";
import { useAuthStore } from "../../state/authStore";
import { Mascot } from "../../components/Mascot";
import { colors, fontFamily, radii } from "../../theme";

type Props = NativeStackScreenProps<ParentStackParamList, "PairTV">;

// Blinking caret for the active (empty) code cell — our custom cell layout
// hides the real input's caret, so the focused field had no visible cursor.
function BlinkingCaret() {
  const opacity = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0,
          duration: 450,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: 450,
          useNativeDriver: Platform.OS !== "web",
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);
  return <Animated.View style={[styles.caret, { opacity }]} />;
}

const CODE_LEN = 6;

export default function PairTvScreen({ navigation, route }: Props) {
  // Deep-link from TV's QR may pass `prefillCode` so the parent doesn't
  // have to retype the 6 digits.
  const prefill =
    typeof (route?.params as any)?.prefillCode === "string"
      ? ((route.params as any).prefillCode as string)
          .slice(0, CODE_LEN)
          .toUpperCase()
      : "";
  const [code, setCode] = useState(prefill);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [focused, setFocused] = useState(false);
  const { accessToken } = useAuthStore();
  const inputRef = useRef<TextInput>(null);

  const { width: winW, height } = useWindowDimensions();

  // The six code cells used fixed 46px widths; on a ~360–390px phone the row
  // (≈324px) was wider than the card's inner width (≈294px) so the end cells
  // spilled outside the card. Size each cell to the space actually available
  // inside the card so all six always fit within the container.
  const CELL_GAP = 6;
  const cardInnerW = Math.min(winW - 40, 440) - 56; // root px(20·2) + card px(28·2)
  const CELL_W = Math.max(
    32,
    Math.min(46, Math.floor((cardInnerW - 4 - CELL_GAP * (CODE_LEN - 1)) / CODE_LEN)),
  );
  const CELL_H = Math.round(CELL_W * 1.26);

  const handlePair = async () => {
    setError(null);
    const trimmed = code.trim();
    if (trimmed.length !== CODE_LEN) {
      setError("Please enter the 6-digit code shown on your TV.");
      return;
    }
    if (!accessToken) {
      setError("Please log in first.");
      return;
    }
    setLoading(true);
    try {
      await pairingApi.claim(trimmed, accessToken);
      setSuccess(true);
      setTimeout(() => navigation.goBack(), 1400);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Render the code as six character cells over a single hidden input.
  const cells = Array.from({ length: CODE_LEN }, (_, i) => code[i] ?? "");
  const activeCell = Math.min(code.length, CODE_LEN - 1);

  return (
    <View style={[styles.root, { minHeight: height }]}>
      <TouchableOpacity
        onPress={() => navigation.goBack()}
        style={styles.backBtn}
        activeOpacity={0.7}
      >
        <ChevronLeft size={20} color={colors.primaryInk} strokeWidth={2.4} />
        <Text style={styles.backText}>Back</Text>
      </TouchableOpacity>

      <View style={styles.content}>
        <View style={styles.card}>
          <View style={styles.iconCircle}>
            <Tv size={26} color={colors.primary} strokeWidth={2.2} />
          </View>

          <Text style={styles.title}>Pair your TV</Text>
          <Text style={styles.subtitle}>
            Open Kidly on your TV and enter the 6-digit code shown on screen.
          </Text>

          {/* Code field — six cells layered over one hidden input. */}
          <TouchableOpacity
            activeOpacity={1}
            style={styles.cellsRow}
            onPress={() => inputRef.current?.focus()}
          >
            {cells.map((ch, i) => {
              const isActive = focused && i === activeCell && !success;
              return (
                <View
                  key={i}
                  style={[
                    styles.cell,
                    {
                      width: CELL_W,
                      height: CELL_H,
                      marginRight: i === CODE_LEN - 1 ? 0 : CELL_GAP,
                    },
                    ch !== "" && styles.cellFilled,
                    isActive && styles.cellActive,
                  ]}
                >
                  {ch !== "" ? (
                    <Text style={styles.cellText}>{ch}</Text>
                  ) : isActive ? (
                    <BlinkingCaret />
                  ) : null}
                </View>
              );
            })}
            <TextInput
              ref={inputRef}
              style={styles.hiddenInput}
              value={code}
              onChangeText={(t) =>
                setCode(
                  t
                    .replace(/[^0-9A-Za-z]/g, "")
                    .toUpperCase()
                    .slice(0, CODE_LEN),
                )
              }
              keyboardType="number-pad"
              maxLength={CODE_LEN}
              autoFocus={Platform.OS === "web"}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              caretHidden
              onSubmitEditing={handlePair}
            />
          </TouchableOpacity>

          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          {success ? (
            <Text style={styles.successText}>Paired! Taking you back…</Text>
          ) : null}

          <TouchableOpacity
            style={[
              styles.btn,
              (loading || success) && styles.btnDisabled,
            ]}
            onPress={handlePair}
            disabled={loading || success}
            activeOpacity={0.85}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.btnText}>Connect TV</Text>
            )}
          </TouchableOpacity>

          <View style={styles.hintRow}>
            <View style={styles.hintMascot}>
              <Mascot mood="happy" color={colors.primary} size={40} />
            </View>
            <Text style={styles.hintText}>
              Can't find the code? On your TV, open Kidly — the pairing screen
              shows a fresh 6-digit code.
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
    paddingHorizontal: 20,
    paddingTop: 48,
    paddingBottom: 32,
  },
  backBtn: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
  },
  backText: {
    fontFamily,
    fontSize: 15,
    fontWeight: "800",
    color: colors.primaryInk,
    marginLeft: 2,
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    backgroundColor: colors.surface,
    borderRadius: radii.xl,
    paddingHorizontal: 28,
    paddingVertical: 32,
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.line,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.08,
    shadowRadius: 28,
    elevation: 6,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.bgWarm,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 18,
  },
  title: {
    fontFamily,
    fontSize: 26,
    fontWeight: "900",
    letterSpacing: -0.4,
    color: colors.ink,
    marginBottom: 8,
    textAlign: "center",
  },
  subtitle: {
    fontFamily,
    fontSize: 14,
    fontWeight: "600",
    lineHeight: 20,
    color: colors.ink2,
    textAlign: "center",
    maxWidth: 320,
    marginBottom: 26,
  },
  cellsRow: {
    flexDirection: "row",
    justifyContent: "center",
    position: "relative",
    marginBottom: 22,
  },
  cell: {
    width: 46,
    height: 58,
    borderRadius: radii.md,
    borderWidth: 2,
    borderColor: colors.lineStrong,
    backgroundColor: colors.bg,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  cellFilled: {
    borderColor: colors.primary,
    backgroundColor: colors.surface,
  },
  cellActive: {
    borderColor: colors.primary,
    backgroundColor: colors.surface,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 3,
  },
  cellText: {
    fontFamily,
    fontSize: 26,
    fontWeight: "900",
    color: colors.ink,
  },
  caret: {
    width: 2,
    height: 28,
    borderRadius: 1,
    backgroundColor: colors.primary,
  },
  // The real input sits invisibly over the cells to capture keystrokes.
  hiddenInput: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0,
    fontSize: 1,
    color: "transparent",
  },
  errorText: {
    fontFamily,
    fontSize: 13,
    fontWeight: "700",
    color: "#D2502E",
    textAlign: "center",
    marginBottom: 14,
  },
  successText: {
    fontFamily,
    fontSize: 14,
    fontWeight: "800",
    color: colors.success,
    textAlign: "center",
    marginBottom: 14,
  },
  btn: {
    width: "100%",
    backgroundColor: colors.primary,
    borderRadius: radii.md,
    paddingVertical: 16,
    alignItems: "center",
    borderBottomWidth: 3,
    borderBottomColor: "rgba(0,0,0,0.18)",
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 5,
  },
  btnDisabled: {
    opacity: 0.7,
  },
  btnText: {
    fontFamily,
    fontSize: 16,
    fontWeight: "900",
    color: "#fff",
    letterSpacing: 0.2,
  },
  hintRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 22,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: colors.line,
  },
  hintMascot: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.bgWarm,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginRight: 12,
  },
  hintText: {
    flex: 1,
    fontFamily,
    fontSize: 12,
    fontWeight: "600",
    lineHeight: 17,
    color: colors.ink3,
  },
});
